import { useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { Section } from '@/components/layout/Section';
import { PageHero } from '@/components/brand/PageHero';
import { Reveal } from '@/components/motion/Reveal';
import { StaggerGroup } from '@/components/motion/StaggerGroup';
import { Button } from '@/components/ui/Button';
import { TextField, TextArea, CheckboxField, Honeypot } from '@/components/ui/Field';
import { careers, openRoles } from '@/content/careers';
import { site } from '@/content/site';
import { careersSchema, type ApiResponse } from '@shared/schemas';
import { ResumeUpload, readResume } from '@/components/ui/ResumeUpload';
import { usePageMeta } from '@/lib/usePageMeta';

const empty = { name: '', email: '', role: '', portfolio: '', note: '', website: '' };

export default function Careers() {
  usePageMeta(
    'Careers — Software Engineering Jobs',
    'Software engineering jobs at Falcon Solutions, a development team in Calgary and Lahore. We hire rarely and carefully, so introduce yourself before a role exists.',
    'Careers',
  );

  const [values, setValues] = useState(empty);
  const [resume, setResume] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const set = (key: keyof typeof empty) => (v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    if (errors.resume) { document.getElementById('resume')?.focus(); return; }

    if (!resume) {
      setErrors((previous) => ({ ...previous, resume: 'Please upload your resume as a PDF.' }));
      document.getElementById('resume')?.focus();
      return;
    }
    // Validate text immediately; the actual attachment is read below and validated by the server.
    const parsed = careersSchema.omit({ resume: true }).safeParse({ ...values, consent });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      requestAnimationFrame(() => document.getElementById(Object.keys(fieldErrors)[0])?.focus());
      return;
    }

    setStatus('sending');
    try {
      const attachment = await readResume(resume);
      const res = await fetch('/api/careers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...parsed.data, resume: attachment }),
      });
      const data: ApiResponse = await res.json();

      if (!res.ok || !data.ok) {
        if ('errors' in data && data.errors) setErrors(data.errors);
        setFormError(('message' in data && data.message) || 'Something went wrong. Please try again.');
        setStatus('error');
        return;
      }

      setStatus('sent');
      setValues(empty);
      setResume(null);
      setConsent(false);
    } catch (error) {
      setFormError(error instanceof Error && error.message.includes('resume') ? error.message : 'Could not reach the server. Please try again, or email us directly.');
      setStatus('error');
    }
  }

  return (
    <>
      <PageHero label="Careers at Falcon" headline="Good work. Shared ownership."
        description="Build software people depend on, with a team that gives you the context and space to do it well. Based in Calgary and Lahore."
        action="Introduce yourself" target="#apply"
        note={openRoles.length ? `${openRoles.length} open ${openRoles.length === 1 ? 'role' : 'roles'} · Explore opportunities below` : 'No open roles right now. Open applications are welcome.'} />
      <nav aria-label="Careers sections" className="page-jump-nav">
        <div className="shell flex flex-wrap gap-x-8 gap-y-1">
          <a href="#culture">Life at Falcon</a><a href="#workplace">How we work</a><a href="#roles">Opportunities</a><a href="#apply">Apply</a>
        </div>
      </nav>

      {/* Culture */}
      <Section id="culture">
        <Reveal>
          <p className="text-eyebrow opacity-65">{careers.culture.eyebrow}</p>
          <h2 className="text-display-md mt-4 max-w-[16ch]">{careers.culture.heading}</h2>
        </Reveal>

        <StaggerGroup className="culture-grid mt-12 grid gap-0 sm:grid-cols-2">
          {careers.culture.items.map((item) => (
            <div key={item.title} className="culture-item p-7 md:p-10">
              <h3
                className="font-display text-xl font-bold"
                style={{ fontStretch: '100%' }}
              >
                {item.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed opacity-70">{item.body}</p>
            </div>
          ))}
        </StaggerGroup>
      </Section>

      {/* Hybrid workplace */}
      <Section id="workplace" tone="dark" pattern>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-6">
            <Reveal>
              <p className="text-eyebrow text-white/65">{careers.hybrid.eyebrow}</p>
              <h2 className="text-display-md mt-4">{careers.hybrid.heading}</h2>
              <p className="mt-6 text-lg leading-relaxed text-white/65">{careers.hybrid.body}</p>
            </Reveal>
          </div>

          <div className="min-w-0 lg:col-span-5 lg:col-start-8">
            <StaggerGroup className="space-y-10" stagger={0.09}>
              <div className="border-t border-white/15 pt-6">
                <p className="text-eyebrow text-white/65">
                  {careers.hybrid.onSite.label}
                </p>
                <p
                  className="font-display mt-3 text-3xl font-bold text-accent"
                  style={{ fontStretch: '100%' }}
                >
                  {careers.hybrid.onSite.value}
                </p>
              </div>

              <div className="border-t border-white/15 pt-6">
                <p className="text-eyebrow text-white/65">
                  {careers.hybrid.benefits.label}
                </p>
                <ul className="mt-4 space-y-3">
                  {careers.hybrid.benefits.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <svg
                        viewBox="0 0 24 24"
                        className="mt-1 h-4 w-4 shrink-0 text-accent"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span className="text-white/75">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerGroup>
          </div>
        </div>
      </Section>

      {/* Roles: real listings when there are any, honest empty state otherwise */}
      <Section id="roles" tone="muted">
        {openRoles.length > 0 ? (
          <>
            <Reveal>
              <h2 className="text-display-md">Open roles</h2>
            </Reveal>
            <div className="mt-12 max-w-[70ch]">
              {openRoles.map((role) => (
                <Reveal key={role.slug}>
                  <details className="group border-b border-hairline">
                    {/* -mx-4 px-4: see the FAQ accordion in Contact.tsx. */}
                    <summary className="hover-summary -mx-4 flex cursor-pointer list-none items-center justify-between gap-6 px-4 py-6">
                      <span>
                        <span
                          className="font-display block text-xl font-bold"
                          style={{ fontStretch: '100%' }}
                        >
                          {role.title}
                        </span>
                        <span className="mt-1 block text-sm opacity-55">
                          {role.location} · {role.type}
                        </span>
                      </span>
                      <svg
                        viewBox="0 0 24 24"
                        className="h-5 w-5 shrink-0 transition-transform duration-400 ease-brand group-open:rotate-45"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </summary>
                    <div className="pb-8">
                      <p className="text-sm leading-relaxed opacity-75">{role.summary}</p>
                      {[{ title: 'What you will do', items: role.responsibilities }, { title: 'What you will bring', items: role.requirements }].map((group) => group.items.length > 0 && (
                        <div key={group.title} className="mt-6"><h3 className="text-lg">{group.title}</h3><ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed">{group.items.map((item) => <li key={item}>{item}</li>)}</ul></div>
                      ))}
                      <a href="#apply" onClick={() => setValues((previous) => ({ ...previous, role: role.title }))} className="mt-6 inline-flex min-h-11 items-center font-medium text-brand underline underline-offset-4">Apply for this role</a>
                    </div>
                  </details>
                </Reveal>
              ))}
            </div>
          </>
        ) : (
          <Reveal>
            <div className="max-w-[60ch]">
              <h2 className="text-display-md">{careers.emptyState.heading}</h2>
              <p className="text-body-lg mt-5 opacity-75">{careers.emptyState.body}</p>
              <a className="mt-6 inline-flex min-h-11 items-center font-medium text-brand underline underline-offset-4" href="#apply">Send an open application</a>
            </div>
          </Reveal>
        )}
      </Section>

      {/* Open application */}
      <Section id="apply">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="text-display-md">{careers.openApplication.heading}</h2>
              <p className="text-body-lg mt-5 opacity-75">{careers.openApplication.body}</p>
              <div className="mt-8 border-l-2 border-brand-100 pl-5">
                <h3 className="text-lg">After you apply</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/70">You will see a confirmation when your application is received. We keep your details on file and get in touch when there is a suitable opportunity.</p>
              </div>
              <a
                href={`mailto:${site.email.careers}`}
                className="font-display mt-7 inline-flex min-h-11 items-center text-xl font-bold transition-colors hover:text-brand"
                style={{ fontStretch: '100%' }}
              >
                {site.email.careers}
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {status === 'sent' ? (
              <Reveal>
                <div role="status" className="rounded-card border border-hairline bg-surface-muted p-10">
                  <h3 className="text-display-md">Application received</h3>
                  <p className="text-body-lg mt-4 opacity-75">
                    Thanks — we have got it. We keep applications on file and will be in touch when
                    something fits.
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={onSubmit} noValidate className="application-panel relative space-y-6">
                  <p className="text-sm text-ink/65">Tell us a little about yourself. Fields marked * are required.</p>
                  <Honeypot value={values.website} onChange={set('website')} />

                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextField
                      name="name"
                      label="Your name"
                      required
                      value={values.name}
                      onChange={set('name')}
                      error={errors.name}
                    />
                    <TextField
                      name="email"
                      label="Email"
                      type="email"
                      required
                      value={values.email}
                      onChange={set('email')}
                      error={errors.email}
                    />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextField
                      name="role"
                      label="Your area of expertise"
                      required
                      placeholder="e.g. Frontend engineer"
                      value={values.role}
                      onChange={set('role')}
                      error={errors.role}
                    />
                    <TextField
                      name="portfolio"
                      label="Portfolio or GitHub"
                      type="url"
                      placeholder="https://"
                      value={values.portfolio}
                      onChange={set('portfolio')}
                      error={errors.portfolio}
                    />
                  </div>

                  <TextArea
                    name="note"
                    label="Tell us about your work"
                    required
                    rows={5}
                    value={values.note}
                    onChange={set('note')}
                    error={errors.note}
                    placeholder="What have you built? What kind of work do you want to be doing?"
                  />

                  <ResumeUpload file={resume} onChange={setResume} error={errors.resume}
                    disabled={status === 'sending'} onError={(message) => setErrors((previous) => ({ ...previous, resume: message }))} />

                  <CheckboxField
                    name="consent"
                    label="I'm happy for Falcon Solutions to keep my application, including my resume, for up to two years for future openings."
                    checked={consent}
                    onChange={(v) => {
                      setConsent(v);
                      if (errors.consent) setErrors((p) => ({ ...p, consent: '' }));
                    }}
                    error={errors.consent}
                  />

                  {/*
                    Outside the CheckboxField rather than inside its label: a
                    link within a <label> steals clicks meant to tick the box.
                  */}
                  <p className="-mt-2 text-xs leading-relaxed opacity-55">
                    Your application and resume are emailed to our hiring team. You can request deletion by email. See our{' '}
                    <Link
                      href="/privacy"
                      className="underline underline-offset-2 transition-opacity hover:opacity-70"
                    >
                      privacy policy
                    </Link>
                    .
                  </p>

                  {formError && (
                    <p role="alert" className="text-sm text-red-600">
                      {formError}
                    </p>
                  )}

                  <Button type="submit" size="lg" disabled={status === 'sending'}>
                    {status === 'sending' ? 'Sending…' : 'Send application'}
                  </Button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
